import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { createPortal } from "react-dom";
import { ReactComponent as CloseIcon } from "images/profile/Close.svg";

function SidePanel({
  isOpen,
  title = "",
  onClose = () => {},
  width = 416, // px
  children,
  headerRight = null,
  className = "",
}) {
  const [shown, setShown] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
      setShouldRender(true);
      // trigger slide-in after mount
      const raf = requestAnimationFrame(() => setShown(true));
      return () => cancelAnimationFrame(raf);
    } else {
      document.body.style.overflow = "";
      setShown(false);
      // delay unmount to allow close animation
      const timeout = setTimeout(() => setShouldRender(false), 300);
      return () => clearTimeout(timeout);
    }
  }, [isOpen]);

  if (!shouldRender) return null;

  const portalEl = document.getElementById("portal") || document.body;

  return createPortal(
    <div className="fixed inset-0 z-[100]">
      <div
        className={`absolute inset-0 transition-opacity duration-300 ease-out`}
        style={{
          background: "var(--Base-Overlay, rgba(29, 27, 34, 0.40))",
          opacity: shown ? 1 : 0,
        }}
        onClick={onClose}
        aria-hidden="true"
      />

      <div
        className={`absolute top-0 right-0 h-full bg-white shadow-xl transform transition-transform duration-300 ease-out ${
          shown ? "translate-x-0" : "translate-x-full"
        } ${className}`}
        style={{ width }}
        role="dialog"
        aria-modal="true"
      >
        <div className="flex items-center justify-between px-[24px] py-[20px] border-b border-[#E4E4E7]">
          <div className="text-[#1D1B22] text-[24px] font-semibold">
            {title}
          </div>
          <div className="flex items-center gap-2">
            {headerRight}
            <button
              className="p-1 rounded hover:bg-[#F3F5F7]"
              aria-label="Close"
              onClick={onClose}
            >
              <CloseIcon className="w-[24px] h-[24px]" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="h-[calc(100%-57px)] overflow-y-auto">{children}</div>
      </div>
    </div>,
    portalEl
  );
}

SidePanel.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  title: PropTypes.string,
  onClose: PropTypes.func,
  width: PropTypes.number,
  children: PropTypes.node,
  headerRight: PropTypes.node,
  className: PropTypes.string,
};

export default SidePanel;
